import { useCallback, useEffect, useMemo, useState } from "react";
import { useApiResource } from "../lib/useApiResource";
import { useAuth } from "../context/AuthProvider";
import {
  createEvent,
  deleteEvent,
  loadEvents,
  type CreateEventInput,
  type CreateEventOutcome,
  type EventDeleteDisposition,
  type TuneupDisposition,
  type WorkoutsDisposition,
  type EventDTO
} from "../lib/eventsApi";

/** Delete options threaded to `deleteEvent` — the own-journey `disposition`
 *  (J7 archive/keep), the nested-tune-up `tuneupDisposition` (S4/C4, §9.3),
 *  plus `workouts` (remove the event's linked calendar workouts too). */
export type EventRemoveOpts = {
  disposition?: EventDeleteDisposition;
  tuneupDisposition?: TuneupDisposition;
  workouts?: WorkoutsDisposition;
};

export type UseEventMutationsResult = {
  /** Create via catalog link or custom body — customer-only; resolves with row + dedupe flag. */
  create: (input: CreateEventInput) => Promise<CreateEventOutcome>;
  /**
   * Delete a customer event. `disposition` names what happens to an active
   * race journey referencing it (J7 — archive it, or keep it raceless);
   * `tuneupDisposition` names what happens to nested tune-ups when the deleted
   * goal has milestones in its window (S4/C4, §9.3).
   */
  remove: (id: string, opts?: EventRemoveOpts) => Promise<void>;
};

export type UseEventsResult = UseEventMutationsResult & {
  events: EventDTO[];
  loading: boolean;
  error: string | null;
  /** Re-fetch the list. */
  refresh: () => void;
};

/**
 * Mutation-only variant of `useEvents` — `create` / `remove` without the
 * list fetch. Use this on screens that already have the event list from a
 * different source (e.g. the Dashboard aggregate at `data.events`) or that
 * don't need to read events at all (e.g. onboarding's manual-session form).
 *
 * Refresh after a mutation is the caller's responsibility: Dashboard calls
 * `refetch()` on the dashboard aggregate; screens that don't render events
 * don't need to refresh anything.
 */
export function useEventMutations(): UseEventMutationsResult {
  const { state: authState, refreshCsrf } = useAuth();
  const csrfToken =
    authState.kind === "customer" || authState.kind === "anon"
      ? authState.csrfToken
      : null;

  const create = useCallback(
    async (input: CreateEventInput): Promise<CreateEventOutcome> => {
      if (!csrfToken) throw new Error("Sign in to save an event.");
      return createEvent(input, csrfToken, refreshCsrf);
    },
    [csrfToken, refreshCsrf]
  );

  const remove = useCallback(
    async (id: string, opts?: EventRemoveOpts): Promise<void> => {
      if (!csrfToken) throw new Error("Sign in to remove an event.");
      await deleteEvent(id, csrfToken, refreshCsrf, opts);
    },
    [csrfToken, refreshCsrf]
  );

  return { create, remove };
}

/**
 * Per-domain hook for the events API (`/api/events`) — list + mutations.
 *
 * Anonymous principals get an empty list from the server and `create` rejects
 * (`login_required`) — callers degrade gracefully (journey §9). Screens that
 * only need `create` / `remove` should use `useEventMutations` instead so
 * they don't double-fetch the list.
 */
export function useEvents(): UseEventsResult {
  const { data, loading, error, refetch } = useApiResource(
    (signal) => loadEvents(signal),
    []
  );
  const mutations = useEventMutations();
  // Optimistic-remove cache. Until the DELETE resolves (or fails and rolls
  // back) we hide the id from the rendered list — `useApiResource` only
  // refreshes once on success, so without this the row stayed visible
  // through the in-flight DELETE round-trip.
  const [optimisticRemovedIds, setOptimisticRemovedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const events = useMemo(() => {
    const raw = data ?? [];
    if (optimisticRemovedIds.size === 0) return raw;
    return raw.filter((e) => !optimisticRemovedIds.has(e.id));
  }, [data, optimisticRemovedIds]);

  // Prune optimistic flags once the refreshed list no longer contains the id.
  // Clearing any earlier (e.g. right after the DELETE resolves) re-exposes the
  // stale row for the duration of the refetch round-trip — the deleted event
  // flashed back before disappearing again.
  useEffect(() => {
    if (!data || optimisticRemovedIds.size === 0) return;
    const present = new Set(data.map((e) => e.id));
    const stillNeeded = [...optimisticRemovedIds].filter((id) => present.has(id));
    if (stillNeeded.length !== optimisticRemovedIds.size) {
      setOptimisticRemovedIds(new Set(stillNeeded));
    }
  }, [data, optimisticRemovedIds]);

  const create = useCallback(
    async (input: CreateEventInput): Promise<CreateEventOutcome> => {
      const res = await mutations.create(input);
      refetch();
      return res;
    },
    [mutations, refetch]
  );

  const remove = useCallback(
    async (id: string, opts?: EventRemoveOpts): Promise<void> => {
      // Optimistic: hide the row immediately. On success, refetch hydrates
      // the canonical state and we clear the optimistic flag. On failure,
      // restore by clearing the flag (the canonical data still has it).
      setOptimisticRemovedIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      try {
        await mutations.remove(id, opts);
        // Keep the optimistic flag through the refetch — the prune effect above
        // clears it once the refreshed data confirms the row is gone.
        refetch();
      } catch (err) {
        // Rollback — let the next render show the row again.
        setOptimisticRemovedIds((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        throw err;
      }
    },
    [mutations, refetch]
  );

  return {
    events,
    loading,
    error,
    refresh: refetch,
    create,
    remove
  };
}
